import { clsx, type ClassValue } from "clsx";
import { saveAs } from "file-saver";

export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

export function capitalize(text: string) {
  if (text.length === 0) {
    return "";
  }

  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
}
export function hexToRgba(hex: string, opacity: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}
export const formatDuration = (seconds: number | null): string => {
  if (!seconds) return "0:00";
  const totalSeconds = Math.floor(seconds); // ensures we only have whole seconds
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${secs.toString().padStart(2, "0")}`;
};

export function isValidFileType(fileKey: string, fileType: string) {
  return fileKey.toLowerCase().endsWith(fileType.toLowerCase());
}

// New releases carry this title until the artist names them.
export const UNTITLED_RELEASE_TITLE = "Untitled Release";

export function isReleaseNamed(title: string | null | undefined): boolean {
  const name = title?.trim().toLowerCase();
  return !!name && name !== UNTITLED_RELEASE_TITLE.toLowerCase();
}

export type AlbumReleaseType = "SINGLE" | "SUPER_SINGLE" | "ALBUM";

type ReleaseTypeSource = {
  releaseType?: AlbumReleaseType | null;
  /** Chosen at creation; null for releases from before kinds. */
  kind?: "SINGLE" | "ALBUM" | null;
  trackCount?: number | null;
  tracks?: readonly { extraAssets?: readonly unknown[] }[];
};

const RELEASE_TYPE_LABELS: Record<AlbumReleaseType, string> = {
  SINGLE: "Single",
  SUPER_SINGLE: "Super Single",
  ALBUM: "Album",
};

const releaseTrackCount = (album: ReleaseTypeSource) =>
  album.tracks?.length ?? album.trackCount ?? 0;

// A Single (or an untyped one-track release) with extra versions is a Super
// Single (LUT-901). Only the album detail carries the extras; lists fall back
// to the track count.
export function albumReleaseType(album: ReleaseTypeSource): AlbumReleaseType {
  if (album.releaseType) return album.releaseType;
  if (album.kind === "ALBUM") return "ALBUM";
  if (album.kind !== "SINGLE" && releaseTrackCount(album) !== 1) return "ALBUM";
  return album.tracks?.[0]?.extraAssets?.length ? "SUPER_SINGLE" : "SINGLE";
}

// Super Singles ship extra versions, so they never sell below this.
export const SUPER_SINGLE_MIN_PRICE = 5;

// "Single", "Super Single" or "Album" — for copy that names the release type.
export function albumTypeLabel(album: ReleaseTypeSource): string {
  return RELEASE_TYPE_LABELS[albumReleaseType(album)];
}

const NEW_RELEASE_MS = 7 * 24 * 60 * 60 * 1000;

// "New" for a week after it went out: the later of release and publish, so
// back-dated releases count from publishing and scheduled ones from release.
export function isNewRelease(
  album: { releaseDate?: string | null; publishedAt?: string | null },
  now = Date.now(),
): boolean {
  const times = [album.releaseDate, album.publishedAt]
    .filter((date): date is string => !!date)
    .map((date) => new Date(date).getTime());
  if (!times.length) return false;
  const age = now - Math.max(...times);
  return age >= 0 && age < NEW_RELEASE_MS;
}

// Same, but albums show their track count instead.
export function albumTrackCountLabel(
  album: ReleaseTypeSource,
  options?: { capitalize?: boolean },
): string {
  const type = albumReleaseType(album);
  if (type !== "ALBUM") return RELEASE_TYPE_LABELS[type];
  return `${releaseTrackCount(album)} ${options?.capitalize ? "Tracks" : "tracks"}`;
}

export function isValidImageType(fileType: string): boolean {
  const allowedMimeTypes = ["image/jpeg", "image/jpg", "image/png"];
  return allowedMimeTypes.includes(fileType.toLowerCase());
}

export function isValidAudioFileType(fileType: string): boolean {
  const allowedMimeTypes = [
    "audio/wav",
    "audio/x-wav",
    "audio/mpeg",
    "audio/aiff",
    "audio/x-aiff",
    "application/zip",
    "application/x-zip-compressed",
  ];
  return allowedMimeTypes.includes(fileType.toLowerCase());
}

export function isValidFileSize(fileSize: number, maxSize: number): boolean {
  const MAX_SIZE = maxSize * 1024 * 1024; // maxSize in MB
  return fileSize <= MAX_SIZE;
}

export function isZip(file: File | { type: string; name: string }): boolean {
  return (
    file.type === "application/zip" ||
    file.type === "application/x-zip-compressed" ||
    file.name.toLowerCase().endsWith(".zip")
  );
}

export function isFirstCharLower(str: string): boolean {
  if (!str) return false;
  const first = str[0];
  return first === first.toLowerCase() && first !== first.toUpperCase();
}

export function get(
  key: string,
  type?: "array" | "object" | "string" | "number" | "boolean",
) {
  try {
    const data = window.localStorage.getItem(key);

    if (!data) {
      if (type === "array") return [];
      if (type === "object") return {};
      return null;
    }

    const parsed = JSON.parse(data);

    // Enforce fallback type if needed
    if (type === "array" && !Array.isArray(parsed)) return [];
    if (
      type === "object" &&
      (typeof parsed !== "object" || Array.isArray(parsed))
    )
      return {};
    return parsed;
  } catch {
    if (type === "array") return [];
    if (type === "object") return {};
    return null;
  }
}

export function save(key: string, value: unknown) {
  try {
    const serialized =
      typeof value === "string" ? value : JSON.stringify(value);
    window.localStorage.setItem(key, serialized);
  } catch {
    // Failed to save to localStorage
  }
}

export function formatTime(seconds: number) {
  if (!seconds) return "00:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
}

export function formatMSTime(ms?: number | null) {
  if (!ms || Number.isNaN(ms)) {
    return "0:00";
  }
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = `${totalSeconds % 60}`.padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  if (bytes < 1024 * 1024 * 1024)
    return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)}GB`;
}

export async function downloadFile(url: string, filename: string) {
  if (!url) {
    return false;
  }

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download request failed (${response.status})`);
    }
    const blob = await response.blob();
    saveAs(blob, filename);
    return true;
  } catch (error) {
    console.error("Failed to download file", error);
    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.rel = "noopener noreferrer";
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (fallbackError) {
      console.error("Fallback download also failed", fallbackError);
      return false;
    }
  }
}

export function truncateText(
  text: string | null | undefined,
  maxLength: number,
): { truncated: string; isTruncated: boolean } {
  if (!text) {
    return { truncated: "", isTruncated: false };
  }
  if (text.length <= maxLength) {
    return { truncated: text, isTruncated: false };
  }
  // Find the last space before the maxLength to avoid cutting words
  const truncated = text.slice(0, maxLength);
  const lastSpaceIndex = truncated.lastIndexOf(" ");
  const truncatedText =
    lastSpaceIndex > 0
      ? truncated.slice(0, lastSpaceIndex).trim()
      : truncated.trim();
  return {
    truncated: truncatedText,
    isTruncated: true,
  };
}
export function ensureHttpsUrl(url: string | null | undefined): string {
  if (!url || !url.trim()) {
    return "";
  }

  const raw = url.trim();

  // Ensure protocol is present so URL parsing works
  const hasProtocol = /^https?:\/\//i.test(raw);
  const candidate = hasProtocol ? raw : `https://${raw}`;

  try {
    const parsed = new URL(candidate);

    // Enforce HTTPS if the URL is using HTTP
    if (parsed.protocol === "http:") {
      parsed.protocol = "https:";
    }

    return parsed.toString();
  } catch {
    // Fallback: ensure we at least return an HTTPS URL string
    if (/^https?:\/\//i.test(raw)) {
      return raw.replace(/^http:\/\//i, "https://");
    }
    return `https://${raw}`;
  }
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Checks if a URL is from a different origin than the current page
 * Returns true if cross-origin, false if same-origin or if URL parsing fails
 */
export function isCrossOriginUrl(url: string | null | undefined): boolean {
  if (!url || typeof window === "undefined") {
    return false;
  }

  try {
    const urlObj = new URL(url, window.location.href);
    return urlObj.origin !== window.location.origin;
  } catch {
    // If URL parsing fails, assume same-origin to avoid CORS issues
    return false;
  }
}
